import { useState, useEffect } from "react";

import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";
import Pagination from "../../../components/Pagination/Pagination";
import {
  ReportActionButtons,
  SECTION_KEYS,
} from "../../screened-manuscripts/components/ReportActionButtons";
import {
  ApplyAction,
  ColorizeStatusText,
} from "../../../components/ApplyAction";
import {
  convertISOToReadableDate,
  getAmbientScreeningSubmissionById,
  showErrorToast,
} from "../../../helpers";
import { DataTable, useLoader } from "stm-frontend-library";
import { EmptyInbox, SignalReportModal } from "../../../components";
import { dropdownOptions } from "../dropdownOptions";
import { columns } from "../columns";
import useDuplicateSubmissions from "../hooks/useDuplicateSubmissions";
import useAmbientScreenedManuscripts from "../../screened-manuscripts/hooks/useAmbientScreenedManuscripts";
import { exportManuscriptsToExcel } from "../export";
import ConfirmationDialog from "../../../components/ConfirmationDialog/ConfirmationDialog";

const ManuscriptsTab = ({ type, isActiveTab, exportRef, tabKey }) => {
  const [selectedItem, setSelectedItem] = useState({
    index: null,
    visibleSection: SECTION_KEYS.ALL,
  });

  const { showLoader, hideLoader, isLoading } = useLoader();
  const [fetchedData, setFetchedData] = useState(null);

  const isInboxTabSelected = type === "wo_status";

  const {
    data,
    fetchData,
    setData,
    setTriggerOrMatchStatus,
    error,
    pageCount,
    currentPage,
    setCurrentPage,
    fetchAllDataForExport,
  } = useDuplicateSubmissions({ type, isActiveTab });

  const { setSubmissionStatus } = useAmbientScreenedManuscripts({
    type: "all",
    isActiveTab: true,
  });

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    actionType: null,
    actionContext: null
  });

  const updateStatus = async (index, status, additionalParams = {}) => {
    const newData = [...data];

    if (isInboxTabSelected) {
      const previousElement = { ...newData[index] };
      newData.splice(index, 1);
      setData(newData);

      const { match_id, persistent_id } = previousElement;
      try {
        await setTriggerOrMatchStatus(match_id, persistent_id, status, additionalParams);
        await fetchData();
      } catch {
        showErrorToast();
        newData.splice(index, 0, previousElement);
        setData(newData);
      }
    } else {
      const previousStatus = newData[index].submission_status;
      newData[index].submission_status = status;

      const { match_id, persistent_id } = newData[index];
      try {
        await setTriggerOrMatchStatus(match_id, persistent_id, status, additionalParams);
      } catch {
        showErrorToast();
        newData[index].submission_status = previousStatus;
      } finally {
        setData(newData);
      }
    }
  };

  const handleIconClick = (index, buttonKey) => {
    setSelectedItem({ index, visibleSection: buttonKey });
  };

  const handleRowClick = (index) => {
    if (index === null) {
      return;
    }
    setSelectedItem({ index, visibleSection: SECTION_KEYS.DUPSUB });
  };

  const handleInterceptedAction = (index, value) => {
    const actionsRequiringConfirmation = [
      "duplicate confirmed",
      // "suspect paper mill"
    ];

    if (actionsRequiringConfirmation.includes(value)) {
      setConfirmationDialog({
        isOpen: true,
        actionType: value,
        actionContext: { index, value }
      });
      return;
    }

    onActionSelect(index, value);
  };

  const handleConfirmationApply = (options) => {
    const { actionContext } = confirmationDialog;

    if (actionContext) {
      onActionSelect(actionContext.index, actionContext.value, options);
    }

    setConfirmationDialog({
      isOpen: false,
      actionType: null,
      pendingAction: null
    });
  };

  const handleConfirmationClose = () => {
    setConfirmationDialog({
      isOpen: false,
      actionType: null,
      pendingAction: null
    });
  };

  const onActionSelect = (index, status, additionalParams = {}) => {
    updateStatus(index, status, additionalParams);
  };

  const onActionUndo = (index) => {
    updateStatus(index, null);
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const dataWithActions = data?.map((element, index) => ({
    ...element,
    raw_match_status: element.match_status,
    timestamp: convertISOToReadableDate(element.timestamp, "DD MMM YYYY"),
    match_status: <ColorizeStatusText statusText={element.match_status} />,
    action: (
      <div className="d-flex">
        <ApplyAction
          index={index}
          status={data[index].submission_status}
          selectAction={handleInterceptedAction}
          undoAction={onActionUndo}
          options={dropdownOptions}
        />
      </div>
    ),
    signals: (
      <ReportActionButtons
        signals={element.signals}
        onIconClick={(buttonKey) => handleIconClick(index, buttonKey)}
      />
    ),
    clickEvent: () => handleRowClick(index),
  }));

  const fetchAmbientScreeningDataIfNeeded = async (index) => {
    const { persistent_id, signals } = data[index];
    if (persistent_id && signals?.some((s) => s !== "duplicateSubmission")) {
      try {
        showLoader();
        const { data: fetchedData } = await getAmbientScreeningSubmissionById(
          persistent_id
        );
        setFetchedData(fetchedData);
      } finally {
        hideLoader();
      }
    }
  };

  const handleExport = async () => {
    try {
      const allData = await fetchAllDataForExport();
      if (allData && allData.length > 0) {
        const fileName = type === "wo_status" ? "InboxManuscripts" : 
                        type === "all" ? "AllManuscripts" : 
                        type === "matched" ? "MatchedManuscripts" : 
                        type === "triggers" ? "TriggerManuscripts" : 
                        "DuplicateSubmissions";
        exportManuscriptsToExcel(allData, fileName);
      }
    } catch (error) {
      showErrorToast("Failed to export data");
    }
  };

  useEffect(() => {
    if (exportRef && tabKey) {
      exportRef.current[tabKey] = handleExport;
    }
  }, [exportRef, tabKey, type, fetchAllDataForExport]);

  useEffect(() => {
    if (selectedItem.index !== null) {
      fetchAmbientScreeningDataIfNeeded(selectedItem.index);
    } else {
      setFetchedData(null);
    }
  }, [selectedItem, data]);

  const resetModalState = () => {
    setFetchedData(null);
    setSelectedItem({
      index: null,
      visibleSection: SECTION_KEYS.ALL,
    });
  };

  const hasNonDupSubSignals =
    selectedItem.index !== null &&
    !isLoading &&
    data[selectedItem.index]?.signals?.some(
      (x) => x !== "duplicateSubmissions"
    );

  const shouldRenderModal =
    selectedItem.index !== null &&
    (fetchedData || data[selectedItem.index]) &&
    (hasNonDupSubSignals ? fetchedData : true);

  return (
    <>
      <ConfirmationDialog
        actionType={confirmationDialog.actionType}
        isOpen={confirmationDialog.isOpen}
        onClose={handleConfirmationClose}
        onApply={handleConfirmationApply}
      />

      {shouldRenderModal && (
        <SignalReportModal
          dupSubData={data[selectedItem.index]}
          contentData={fetchedData}
          visibleSection={selectedItem.visibleSection}
          onClose={resetModalState}
          setTriggerOrMatchStatus={setTriggerOrMatchStatus}
          setSubmissionStatus={setSubmissionStatus}
          fetchData={fetchData}
        />
      )}
      {error && <ErrorMessage />}
      {!error && (
        <>
          <DataTable
            columns={columns}
            rows={dataWithActions}
            customNoDataComponent={isInboxTabSelected ? EmptyInbox : null}
            isLoading={isLoading}
          />
          <Pagination
            handlePageClick={handlePageClick}
            pageCount={pageCount}
            currentPage={currentPage}
          />
        </>
      )}
    </>
  );
};

export default ManuscriptsTab;